/**
 * Global axios wiring for bearer-token auth.
 *
 * The backend now requires a signed, expiring JWT on every /api route, so we
 * attach it to every request and sign the user out automatically when it is
 * rejected (expired token, revoked account).
 */
import axios from 'axios';
import { toast } from 'sonner';
import { authStorage } from '@/utils/authStorage';

let redirecting = false;

export function applyAuthToken(token) {
  if (token) {
    axios.defaults.headers.common.Authorization = `Bearer ${token}`;
  } else {
    delete axios.defaults.headers.common.Authorization;
  }
}

export function setupAxios() {
  applyAuthToken(authStorage.getAuthorToken());

  axios.interceptors.request.use((config) => {
    const token = authStorage.getAuthorToken();
    if (token) {
      config.headers = config.headers || {};
      if (!config.headers.Authorization) config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  axios.interceptors.response.use(
    (res) => res,
    (error) => {
      const status = error?.response?.status;
      const url = error?.config?.url || '';
      if (status === 402) {
        const detail = error?.response?.data?.detail;
        const msg = (typeof detail === 'object' ? detail?.error : detail)
          || 'Your plan limit is reached. Upgrade to continue.';
        toast.error(msg, {
          action: {
            label: 'See plans',
            onClick: () => window.location.assign('/thesis/billing'),
          },
        });
      }
      const isAuthCall = url.includes('/auth/login') || url.includes('/auth/register')
        || url.includes('/auth/forgot-password') || url.includes('/auth/reset-password');
      if (status === 401 && !isAuthCall && !redirecting) {
        redirecting = true;
        authStorage.clearAuthorAuth();
        applyAuthToken(null);
        if (typeof window !== 'undefined' && !window.location.pathname.includes('/login')) {
          window.location.assign('/thesis/login?expired=1');
        }
      }
      return Promise.reject(error);
    },
  );
}
